import { X, Check, Search, QrCode, Shield, Zap } from "lucide-react";

export function AboutProblemSolutionSection() {
  return (
    <section className="py-24 bg-card">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-lg text-foreground font-semibold text-sm mb-6">
            <Search className="w-4 h-4 text-foreground" />
            <span className="text-foreground">Problem & Solution</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            The <span className="text-primary">Problem</span> We Solve
          </h2>
          <p className="text-xl text-foreground/80 max-w-3xl mx-auto">
            Traditional lost & found methods are broken. We've built a modern solution that actually works.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Problem Side */}
          <div className="space-y-8">
            <div className="text-center lg:text-left">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Traditional Methods Fail
              </h3>
              <p className="text-foreground/80 mb-8">
                Losing valuable items like metro cards, laptops, or personal belongings is frustrating and costly.
              </p>
            </div>

            <div className="space-y-6">
              <div className="group bg-card p-8 rounded-2xl border-2 border-border hover:border-red-600 transition-all duration-300 hover:shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-red-600/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-red-600/20 transition-all duration-300">
                    <X className="w-4 h-4 text-red-600 group-hover:text-red-700 transition-colors duration-300" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">No Way to Contact Owner</h4>
                    <p className="text-foreground/80 text-sm">When someone finds your item, there's no reliable way for them to reach you.</p>
                  </div>
                </div>
              </div>

              <div className="group bg-card p-8 rounded-2xl border-2 border-border hover:border-red-600 transition-all duration-300 hover:shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-red-600/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-red-600/20 transition-all duration-300">
                    <X className="w-4 h-4 text-red-600 group-hover:text-red-700 transition-colors duration-300" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Lost & Found Centers Don't Work</h4>
                    <p className="text-foreground/80 text-sm">Items sit in lost & found for weeks without any connection to owners.</p>
                  </div>
                </div>
              </div>

              <div className="group bg-card p-8 rounded-2xl border-2 border-border hover:border-red-600 transition-all duration-300 hover:shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-red-600/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-red-600/20 transition-all duration-300">
                    <X className="w-4 h-4 text-red-600 group-hover:text-red-700 transition-colors duration-300" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Expensive Replacements</h4>
                    <p className="text-foreground/80 text-sm">You end up spending money on replacements for items that could be returned.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Solution Side */}
          <div className="space-y-8">
            <div className="text-center lg:text-left">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                <span className="text-primary">Metefy</span> Changes Everything
              </h3>
              <p className="text-foreground/80 mb-8">
                Our digital identity platform creates instant connections between finders and owners.
              </p>
            </div>

            <div className="space-y-6">
              <div className="group bg-card p-8 rounded-2xl border-2 border-border hover:border-green-600 transition-all duration-300 hover:shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-600/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-green-600/20 transition-all duration-300">
                    <Check className="w-4 h-4 text-green-600 group-hover:text-green-700 transition-colors duration-300" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Instant QR Connection</h4>
                    <p className="text-foreground/80 text-sm">Anyone can scan your QR code and immediately connect with you securely.</p>
                  </div>
                </div>
              </div>

              <div className="group bg-card p-8 rounded-2xl border-2 border-border hover:border-green-600 transition-all duration-300 hover:shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-600/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-green-600/20 transition-all duration-300">
                    <Check className="w-4 h-4 text-green-600 group-hover:text-green-700 transition-colors duration-300" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Privacy Protected</h4>
                    <p className="text-foreground/80 text-sm">Your personal information stays secure while enabling communication.</p>
                  </div>
                </div>
              </div>

              <div className="group bg-card p-8 rounded-2xl border-2 border-border hover:border-green-600 transition-all duration-300 hover:shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-600/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-green-600/20 transition-all duration-300">
                    <Check className="w-4 h-4 text-green-600 group-hover:text-green-700 transition-colors duration-300" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Fast Recovery</h4>
                    <p className="text-foreground/80 text-sm">Get your belongings back quickly without expensive replacements.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Row */}
        <div className="mt-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                <QrCode className="w-8 h-8 text-primary group-hover:scale-110 transition-all duration-300" />
              </div>
              <h4 className="text-lg font-bold text-foreground mb-2">QR Technology</h4>
              <p className="text-foreground/80">Advanced QR codes that work with any smartphone camera</p>
            </div>

            <div className="text-center group">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                <Shield className="w-8 h-8 text-primary group-hover:scale-110 transition-all duration-300" />
              </div>
              <h4 className="text-lg font-bold text-foreground mb-2">Secure Platform</h4>
              <p className="text-foreground/80">Your privacy and security are our top priority</p>
            </div>

            <div className="text-center group">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                <Zap className="w-8 h-8 text-primary group-hover:scale-110 transition-all duration-300" />
              </div>
              <h4 className="text-lg font-bold text-foreground mb-2">Instant Results</h4>
              <p className="text-foreground/80">Connect with owners immediately when items are found</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
} 